/**
 * Guarded browser-storage helpers.
 *
 * Every read and write is wrapped: storage can throw outright (Safari private
 * mode, cookies blocked, a sandboxed iframe) or simply be unavailable during
 * prerendering. A preference failing to persist must never break the page, so
 * these degrade to "no persistence" instead of raising.
 */

function storage(): Storage | undefined {
	try {
		if (typeof localStorage === 'undefined') return undefined;
		// A write probe: some browsers expose the object but throw on use.
		const probe = '__meadow_probe__';
		localStorage.setItem(probe, probe);
		localStorage.removeItem(probe);
		return localStorage;
	} catch {
		return undefined;
	}
}

export function readStored(key: string): string | undefined {
	try {
		return storage()?.getItem(key) ?? undefined;
	} catch {
		return undefined;
	}
}

export function writeStored(key: string, value: string): void {
	try {
		storage()?.setItem(key, value);
	} catch {
		/* Preference is not persisted; the session still works. */
	}
}

export function removeStored(key: string): void {
	try {
		storage()?.removeItem(key);
	} catch {
		/* Nothing to do. */
	}
}

/** Reads and JSON-parses a value, discarding anything malformed. */
export function readStoredJson<T>(key: string): T | undefined {
	const raw = readStored(key);
	if (raw === undefined) return undefined;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return undefined;
	}
}

export function writeStoredJson(key: string, value: unknown): void {
	try {
		writeStored(key, JSON.stringify(value));
	} catch {
		/* Non-serialisable value: skip persistence rather than throw. */
	}
}
